import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  byNewest,
  canRectify,
  dateTime,
  DEFAULT_PROPERTY,
  divergence,
  DIVERGENCE_THRESHOLD,
  EMPTY,
  nowIso,
  type PriorityRanking,
  type PriorityRectified,
  projectRankings,
  projectRectified,
  RECTIFIED_FOOTER,
  uuidv7,
} from '@kilroy/shared';

import { db } from '../db/db';
import { useUsers } from '../hooks/useUsers';
import { commit } from '../db/commit';
import { useSession } from '../app/session';
import { Sheet } from '../components/Sheet';
import './priorities.css';

/* Brandon's q.05 feature, in his words: "The individual weight and ranking
 * system will allow each team member to assert their priorities to be
 * considered and rectified by Patrick."
 *
 * Two design notes worth keeping:
 *
 * · Reordering is buttons, not drag. Drag-to-reorder is the obvious choice and
 *   the wrong one here — it needs a sustained precise touch, which is exactly
 *   what a glove, a cold morning, or a bumpy truck takes away. Up/down moves
 *   also work from a keyboard and announce themselves to a screen reader for
 *   free. The list is seven items long; nobody is sorting a hundred.
 *
 * · Every move appends a row. There is no "save" — the ordering IS the log,
 *   which is what makes it offline-safe by construction.
 */

export function Priorities() {
  const { user } = useSession();
  // The team as the server knows them. See the note beside `voters` below.
  const { users: team } = useUsers();
  const [rectifying, setRectifying] = useState(false);
  const [note, setNote] = useState('');
  const [order, setOrder] = useState<string[] | null>(null);

  const items = useLiveQuery(
    () => db.priorityItems.where('property_id').equals(DEFAULT_PROPERTY).toArray(),
    [],
    [],
  );
  const rankings = useLiveQuery(() => db.priorityRankings.toArray(), [], []);
  const rectified = useLiveQuery(() => db.priorityRectified.toArray(), [], []);

  const standing = projectRectified(rectified, DEFAULT_PROPERTY);
  const mine = projectRankings(rankings, user.id);

  /* The displayed order: Patrick's rectified list if there is one, otherwise
     everyone's median. Never arbitrary. */
  const displayed = useMemo(() => {
    if (standing?.ordered_item_ids.length) {
      const known = standing.ordered_item_ids.filter((id) => items.some((i) => i.id === id));
      const rest = items.filter((i) => !known.includes(i.id)).map((i) => i.id);
      return [...known, ...rest];
    }
    return [...items]
      .sort((a, b) => (mine.get(a.id) ?? 99) - (mine.get(b.id) ?? 99))
      .map((i) => i.id);
  }, [standing, items, mine]);

  const working = order ?? displayed;

  async function moveMine(itemId: string, direction: -1 | 1) {
    const current = [...items]
      .sort((a, b) => (mine.get(a.id) ?? 99) - (mine.get(b.id) ?? 99))
      .map((i) => i.id);
    const from = current.indexOf(itemId);
    const to = from + direction;
    if (to < 0 || to >= current.length) return;
    current.splice(to, 0, current.splice(from, 1)[0]);

    // One row per item, appended. Nothing is overwritten, so two people
    // reordering offline at the same time both keep their opinion.
    const rows: PriorityRanking[] = current.map((id, i) => ({
      id: uuidv7(),
      item_id: id,
      user_id: user.id,
      rank: i + 1,
      ranked_at: nowIso(),
      sync_state: 'local',
    }));
    await commit(...rows.map((r) => ({ kind: 'ranking' as const, record: r })));
  }

  function moveMaster(index: number, direction: -1 | 1) {
    const next = [...working];
    const to = index + direction;
    if (to < 0 || to >= next.length) return;
    next.splice(to, 0, next.splice(index, 1)[0]);
    setOrder(next);
  }

  async function commitRectify() {
    const record: PriorityRectified = {
      id: uuidv7(),
      project_id: 'kilroy-phase-1',
      property_id: DEFAULT_PROPERTY,
      ordered_item_ids: working,
      rectified_by: user.id,
      rectified_at: nowIso(),
      note: note.trim() || undefined,
      sync_state: 'local',
    };
    await commit({ kind: 'rectified', record });
    setRectifying(false);
    setOrder(null);
    setNote('');
  }

  const mayRectify = canRectify(user.id);

  return (
    <div className="screen">
      <div className="screen__h">
        <h1 className="screen__t">Priorities</h1>
        <p className="screen__s">
          {rectifying ? 'Rectifying the team order' : 'Tap the arrows to set your own ranking'}
        </p>
      </div>

      {items.length === 0 ? (
        <div className="empty">
          <div className="empty__t">{EMPTY.priorities}</div>
          <div className="empty__h">Items are created at the desk, from the map.</div>
        </div>
      ) : (
        <ol className="prio">
          {working.map((itemId, i) => {
            const item = items.find((x) => x.id === itemId);
            if (!item) return null;

            /* The team from the last pull, not the static roster.
             *
             * useUsers exists precisely for this and documents it — the roster
             * is keyed by slug while the server issues uuids — but this screen
             * still reached past it. Every comparison matched nothing, so the
             * dots row showed seven empty circles and the names line said
             * "nobody has ranked this" while twenty rankings sat in the store. */
            const voters = team.filter((u) =>
              rankings.some((r) => r.item_id === itemId && r.user_id === u.id),
            );
            const drift = standing
              ? divergence(itemId, rankings, standing.ordered_item_ids)
              : null;

            return (
              <li key={itemId} className="prio__row">
                <span className="prio__rank mono">{i + 1}</span>

                <div className="grow">
                  <div className="prio__title">{item.title}</div>

                  <div className="prio__who mono">
                    {voters.length === 0 ? (
                      'nobody has ranked this'
                    ) : (
                      voters.map((u, n) => (
                        <span key={u.id} style={{ whiteSpace: 'nowrap' }}>
                          {n > 0 && ' · '}
                          {u.id === user.id ? 'you' : u.name.split(' ')[0]} #
                          {projectRankings(rankings, u.id).get(itemId)}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="prio__dots" aria-hidden="true">
                    {team.map((u) => (
                      <span
                        key={u.id}
                        className={`prio__dot${voters.some((v) => v.id === u.id) ? ' prio__dot--in' : ''}`}
                        title={u.name}
                      />
                    ))}
                    {drift != null && drift >= DIVERGENCE_THRESHOLD && (
                      <span className="prio__drift mono">team ranks this {drift} apart</span>
                    )}
                  </div>
                </div>

                <div className="prio__moves">
                  <button
                    type="button"
                    className="prio__move"
                    aria-label={`Move ${item.title} up`}
                    onClick={() =>
                      rectifying ? moveMaster(i, -1) : void moveMine(itemId, -1)
                    }
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="prio__move"
                    aria-label={`Move ${item.title} down`}
                    onClick={() =>
                      rectifying ? moveMaster(i, 1) : void moveMine(itemId, 1)
                    }
                  >
                    ↓
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {mayRectify && items.length > 0 && (
        <div className="mod">
          {rectifying ? (
            <>
              <button
                type="button"
                className="btn btn--primary btn--lg"
                onClick={() => void commitRectify()}
              >
                Commit rectified order
              </button>
              <input
                className="textarea"
                style={{ minHeight: 'var(--target)' }}
                placeholder="Why this order? (optional, but it’s what people read later)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setRectifying(false);
                  setOrder(null);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn--quiet btn--lg"
              onClick={() => setRectifying(true)}
            >
              Rectify the team order
            </button>
          )}
        </div>
      )}

      <PrioritiesFooter standing={standing} />
    </div>
  );
}

function PrioritiesFooter({ standing }: { standing: PriorityRectified | null }) {
  const { userById } = useUsers();
  const [open, setOpen] = useState(false);
  const history = useLiveQuery(() => db.priorityRectified.toArray(), [], []);

  return (
    <>
      <button type="button" className="prio__footer" onClick={() => setOpen(true)}>
        {standing ? (
          <>
            Rectified by {userById(standing.rectified_by).name.split(' ')[0]} ·{' '}
            {new Date(standing.rectified_at).toLocaleDateString([], {
              month: 'short',
              day: 'numeric',
            })}{' '}
            · every change keeps its why
          </>
        ) : (
          RECTIFIED_FOOTER
        )}
      </button>

      {open && (
        <Sheet title="Rectification history" onClose={() => setOpen(false)}>
          {history.length === 0 ? (
            <div className="empty">
              <div className="empty__t">No rectifications yet.</div>
              <div className="empty__h">
                Until then the list shows the team’s own ranking.
              </div>
            </div>
          ) : (
            <ul className="ledger">
              {[...history]
                .sort(byNewest((r) => r.rectified_at))
                .map((r) => (
                  <li key={r.id}>
                    <span className="ledger__mark">
                      <span className="ledger__dot" />
                    </span>
                    <div>
                      <div className="ledger__verb">
                        {userById(r.rectified_by).name} set the order
                      </div>
                      <div className="ledger__detail">
                        {dateTime(r.rectified_at)}
                      </div>
                      {r.note && <p style={{ marginTop: 6, lineHeight: 1.45 }}>{r.note}</p>}
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </Sheet>
      )}
    </>
  );
}
